import type {
  EmailCitacaoLocal,
  EmailsCitacaoNodeData,
  EmailSubsequenteCitacao,
} from '../types/editor';

export const MAX_EMAILS_CITACAO = 3;

// Faixa espelhada do validador de publicação do core; aqui só antecipa o erro.
export const FAIXA_INTERVALO_EMAIL_CITACAO = { min: 1, max: 720, padrao: 24 } as const;

function gerarEmailCitacaoId(): string {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return `email-citacao-${c.randomUUID()}`;
  return `email-citacao-${Math.random().toString(36).slice(2, 11)}`;
}

/** O 1º e-mail acompanha o disparo e por isso não tem intervalo. */
export function novoEmailCitacao(posicao: number): EmailCitacaoLocal {
  return {
    id: gerarEmailCitacaoId(),
    modeloEmailId: null,
    intervaloHoras: posicao === 0 ? null : FAIXA_INTERVALO_EMAIL_CITACAO.padrao,
  };
}

function lerEmailCitacao(valor: unknown, posicao: number): EmailCitacaoLocal {
  const raw = valor && typeof valor === 'object' ? (valor as Record<string, unknown>) : {};
  return {
    // Id por posição quando ausente: a lista é re-lida a cada render, e um id aleatório
    // remontaria o Select a cada tecla.
    id: typeof raw.id === 'string' && raw.id.length > 0 ? raw.id : `email-citacao-${posicao + 1}`,
    modeloEmailId:
      typeof raw.modeloEmailId === 'string' && raw.modeloEmailId.length > 0
        ? raw.modeloEmailId
        : null,
    intervaloHoras:
      posicao > 0 && typeof raw.intervaloHoras === 'number' ? raw.intervaloHoras : null,
  };
}

/**
 * Lê os e-mails do bloco CITACAO de parâmetros que podem vir de JSON importado, de `node.data`
 * ou de rascunho anterior à lista. Rascunhos antigos só têm `modeloEmailId` (o e-mail de reforço
 * único) e viram uma lista de um item: converter na leitura, e não no carregamento, alcança
 * também os blocos aninhados em ramos sem percorrer a árvore.
 *
 * Não corta acima de `MAX_EMAILS_CITACAO`: o excesso vindo de importação é acusado pela
 * validação, em vez de sumir em silêncio.
 */
export function lerEmailsCitacao(parametros: Record<string, unknown>): EmailCitacaoLocal[] {
  if (Array.isArray(parametros.emailsCitacao)) {
    return parametros.emailsCitacao.map(lerEmailCitacao);
  }
  const legado = parametros.modeloEmailId;
  return typeof legado === 'string' && legado.length > 0
    ? [lerEmailCitacao({ modeloEmailId: legado }, 0)]
    : [];
}

function emailSubsequenteCompleto(email: EmailCitacaoLocal): EmailSubsequenteCitacao | null {
  if (!email.modeloEmailId || typeof email.intervaloHoras !== 'number') return null;
  return { modeloEmailId: email.modeloEmailId, intervaloHoras: email.intervaloHoras };
}

export function compilarEmailsCitacao(emails: EmailCitacaoLocal[]): EmailsCitacaoNodeData {
  const [primeiro, ...seguintes] = emails;
  const emailsSubsequentes = seguintes
    .map(emailSubsequenteCompleto)
    .filter((email): email is EmailSubsequenteCitacao => email !== null);
  return {
    ...(primeiro?.modeloEmailId ? { modeloEmailId: primeiro.modeloEmailId } : {}),
    ...(emailsSubsequentes.length > 0 ? { emailsSubsequentes } : {}),
  };
}

/**
 * O inverso de `compilarEmailsCitacao`, para o caminho sem `editorSource`: o 1º e-mail mora em
 * `modeloEmailId` e os seguintes em `emailsSubsequentes`. `undefined` quando o nó não traz nenhum
 * dos dois, para não apagar a lista que o formato antigo do editor tenha.
 *
 * @example emailsCitacaoDoNode({ modeloEmailId: 'm1', emailsSubsequentes: [{ modeloEmailId: 'm2', intervaloHoras: 24 }] })
 * // [{ id: 'email-citacao-1', modeloEmailId: 'm1', intervaloHoras: null },
 * //  { id: 'email-citacao-2', modeloEmailId: 'm2', intervaloHoras: 24 }]
 */
export function emailsCitacaoDoNode(
  data: Record<string, unknown>,
): EmailCitacaoLocal[] | undefined {
  const primeiro = typeof data.modeloEmailId === 'string' ? data.modeloEmailId : undefined;
  const seguintes = Array.isArray(data.emailsSubsequentes) ? data.emailsSubsequentes : [];
  if (!primeiro && seguintes.length === 0) return undefined;
  return [{ modeloEmailId: primeiro }, ...seguintes].map(lerEmailCitacao);
}

/** Sufixo do resumo do card: `· 3 e-mails (+24h, +48h)`. Vazio quando não há e-mail. */
export function resumoEmailsCitacao(emails: EmailCitacaoLocal[]): string {
  if (emails.length === 0) return '';
  if (emails.length === 1) return ' · 1 e-mail';
  const intervalos = emails
    .slice(1)
    .map((email) => `+${email.intervaloHoras ?? '?'}h`)
    .join(', ');
  return ` · ${emails.length} e-mails (${intervalos})`;
}
